import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { BoundedContent, GridContainer, Row, Column } from '@vp/swan'
import { useSwanStyleKeys } from '@vp/ubik-context'

import { PreviewArea } from '~/app/features/Gallery/components/PreviewArea'
import { FiltersSidebar } from '~/app/features/Gallery/components/Filters/FiltersSidebar'
import { Pagination } from '~/app/features/Gallery/components/Pagination'
import { Subheader } from '~/app/features/Gallery/components/Subheader'
import { HeroHeader } from '~/app/features/Gallery/components/Header/HeroHeader'
import { ActionHeader } from '~/app/features/Gallery/components/Header/ActionHeader'
import { booleanRenderPropertySelector, getMetadata, getIGPL0Url } from '~/app/store/config/reducer'
import { RenderProperty } from 'shared/renderProperties'
import { SecondaryContent } from '~/app/features/Gallery/components/SecondaryContent'
import { Faq } from '~/app/features/Gallery/components/Footer/Faq'
import { ProductOptionsDescriptions } from '~/app/features/Gallery/components/Footer/ProductOptionsDescriptions'
import { QuickLinks } from '~/app/features/Gallery/components/Footer/QuickLinks'
import { IGPL0Callout } from '~/app/features/Gallery/components/Footer/IGPL0Callout'
import { useInitExperiments } from '~/app/features/Gallery/hooks/useInitExperiments'
import { DesignPersonalizationProvider } from './components/DesignPersonalization'
import { ImageLoadedInstrumenter } from '~/app/features/Gallery/components/ImageLoadedInstrumenter'
import { useUpdateAuthData } from '~/app/features/Gallery/hooks/useUpdateAuthData'
import { SetHighlightingProvider } from '~/app/features/Gallery/components/SetHighlighting/SetHighlightingContext'
import { SWAN_STYLE_KEYS } from '~/app/features/FragmentRoot/constants'
import { SIDEBAR_GRID_SPANS, MAIN_GRID_SPANS, MAIN_GRID_OFFSETS } from '~/app/features/Gallery/constants'
import { useInitDebug } from './hooks/useInitDebug'
import { useInitTracking } from '~/app/features/Gallery/hooks/useInitTracking'
import { getDebugModeEnabled } from '~/app/store/debug'
import { IS_BROWSER } from '~/app/constants'

const DebugToolbar = lazy(() => import('~/app/features/DebugToolbar'))

export const Gallery = () => {
  const galleryMetadata = useSelector(getMetadata)
  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const debugEnabled = useSelector(getDebugModeEnabled)
  const igpL0Url = useSelector(getIGPL0Url)

  // Render props
  const showProductInfo = booleanRenderProperty(RenderProperty.ShowProductInfo)
  const showIGPL0Url = booleanRenderProperty(RenderProperty.ShowTLPL0Quicklink)
  const showQuicklinks = booleanRenderProperty(RenderProperty.ShowQuicklinks)

  const shouldShowIgp0Callout = !showQuicklinks && showIGPL0Url && igpL0Url

  useSwanStyleKeys(SWAN_STYLE_KEYS)

  useInitTracking()
  useInitExperiments()
  useUpdateAuthData()
  useInitDebug()

  return (
    <>
      <BoundedContent className="app" data-testid="gallery-fragment">
        <ImageLoadedInstrumenter />
        <SetHighlightingProvider>
          <DesignPersonalizationProvider>
            <GridContainer>
              <Row>
                <Column span={12}>
                  <HeroHeader />
                  <ActionHeader />
                </Column>
                <Column {...MAIN_GRID_OFFSETS} {...MAIN_GRID_SPANS}>
                  <Subheader />
                </Column>
              </Row>
              <Row>
                <Column className="left-nav-wrapper" {...SIDEBAR_GRID_SPANS}>
                  <FiltersSidebar />
                </Column>
                <Column {...MAIN_GRID_SPANS}>
                  <PreviewArea />
                  <Pagination />
                  {!!galleryMetadata && showProductInfo && <SecondaryContent />}
                  <section className="footer">
                    <Faq />
                  </section>
                  <nav className="external-links">
                    <ProductOptionsDescriptions />
                    <QuickLinks />
                    {shouldShowIgp0Callout && <IGPL0Callout />}
                  </nav>
                </Column>
              </Row>
              {debugEnabled && IS_BROWSER && (
                <Suspense fallback={null}>
                  <DebugToolbar />
                </Suspense>
              )}
            </GridContainer>
          </DesignPersonalizationProvider>
        </SetHighlightingProvider>
      </BoundedContent>
    </>
  )
}

Gallery.displayName = 'Gallery'
